import { HttpRequest } from "../utils"
import { v4 as uuidv4 } from "uuid"

const httpRequest = new HttpRequest()

/**
 * R14 / G18d — Mixed cart split-checkout. Frontend posts pre-grouped per-
 * supplier line bundles; backend routes through the existing OrderLogic.create
 * for each group and tags consignment orders post-creation.
 *
 * Use this instead of /order when the cart contains ANY consignment line —
 * the split logic creates one Order per supplier and applies the correct
 * orderType / source / consignmentAgreementId markers.
 */
export const splitCheckout = async (data: {
  receiverBranchId: string
  receiverStaffId?: string | null
  deliveryType: string
  deliveryDate: string
  senderNote?: string | null
  senderAdditionalNote?: string | null
  send: boolean
  groups: Array<{
    supplierBusinessId: string
    isConsignment: boolean
    consignmentAgreementId?: string | null
    lines: Array<{
      variantId: string
      parentVariantId: string
      warehouseId: string
      quantity: number
      price: number
      discountType?: string | null
      discountValue?: number | null
      consignmentOfferingItemId?: string | null
    }>
  }>
}) => {
  return httpRequest.post("/ord/cart/split-checkout", data as never)
}

export const list = async (data: any) => {
  return httpRequest.post("/ord/order/list", data)
}

export interface IDeliveryTimelineEntry {
  id: string
  actionType: string
  notes: string | null
  occurredAt: string
}

export const deliveryTimeline = async (
  orderId: string,
): Promise<{ rows: IDeliveryTimelineEntry[]; count: number }> => {
  return httpRequest.get(`/ord/order/${orderId}/delivery-timeline`)
}

/**
 * R16 / G18h — Revert a VMI auto-approved order back to REGISTERED so the
 * supplier can re-review. Frontend `[Татгалзах + revert]` action surfaces
 * only when `order.source === "VMI_CRON"` and `orderStatus === "AUTHORIZED"`.
 */
export const vmiRevert = async (id: string, reason?: string | null) => {
  return httpRequest.post(`/ord/order/${id}/vmi-revert`, { reason: reason ?? null })
}

export const count = async (data: { type: string; orderStatus: string }) => {
  return httpRequest.post("/ord/order/count", data)
}

export const excel = async (data: any) => {
  return httpRequest.post("/ord/order/excel", data)
}

export const get = async (id: string) => {
  return httpRequest.get(`/ord/order/${id}`)
}

export const create = async (data: {
  type?: string | null
  myBusinessId?: string | null
  orderedUserId?: string | null
  businessId?: string | null
  paymentTermId?: string | null
  supplierStaffId?: string | null
  buyerStaffId?: string | null
  receiverBranchId?: string | null
  deliveryType?: string | null
  deliveryDate?: string | null
  receiverStaffId?: string | null
  lines?: any[] | null
  additionalLines?: any[] | null
  senderNote?: string | null
  senderAdditionalNote?: string | null
  shippingAmount?: string | null
  discountType?: string | null
  discountValue?: string | null
  advancePercent?: string | null
  attachments?: any[] | null
  toReview?: boolean | null
  send?: boolean | null
}) => {
  return httpRequest.post("/ord/order", data)
}

export const update = async (
  id: string,
  data: {
    businessId: string
    receiverBranchId: string
    deliveryType: string
    deliveryDate: string
    receiverStaffId: string
    lines: any[]
    additionalLines?: any[]
    senderNote: string
    senderAdditionalNote: string
    shippingAmount: string
    discountType: string
    discountValue: string
    attachments: any[]
    toReview: boolean
    send: boolean
  },
) => {
  return httpRequest.put(`/ord/order/${id}`, data)
}

export const cancel = async (id: string) => {
  return httpRequest.put(`/ord/order/${id}/cancel`)
}

export const getCancellability = (id: string) =>
  httpRequest.get(`/ord/order/${id}/cancellability`) as Promise<{ cancellable: boolean; reasons: string[] }>

export const cancelConfirmed = (id: string, reason: string) =>
  httpRequest.post(`/ord/order/${id}/cancel-confirmed`, { reason }, { "Idempotency-Key": uuidv4() })

export const respond = async (id: string, data: { approve: boolean; reason?: string }) => {
  return httpRequest.put(`/ord/order/${id}/respond`, data)
}

export const review = async (id: string, data: { review: boolean }) => {
  return httpRequest.put(`/ord/order/${id}/review`, data)
}

export const setDeliveryLocation = async (id: string, data: { latitude: number; longitude: number }) => {
  return httpRequest.put(`/ord/order/${id}/delivery-location`, data)
}

export const business = async (data: any) => {
  return httpRequest.get(`/ord/order/business_select`, data)
}

export const respond_bulk = async (data: any) => {
  return httpRequest.put(`/ord/order/respond_bulk`, data)
}

export const split = async (id: string, data: { id: string }) => {
  return httpRequest.put(`/ord/order/${id}/split`, data)
}

// Multi-supplier order create - supplier тус бүрт тусдаа order үүсгэх
export const createMulti = async (data: {
  orders: {
    businessId: string
    receiverBranchId: string
    deliveryType: string
    deliveryDate: string
    receiverStaffId: string
    lines: any[]
    additionalLines?: any[]
    senderNote: string
    senderAdditionalNote: string
    shippingAmount: string
    discountType: string
    discountValue: string
    attachments: any[]
    toReview: boolean
    send: boolean
  }[]
}) => {
  return httpRequest.post("/ord/order/multi", data)
}
